/**
 * HormonalStep component - Step 3 of the 4-step check-in flow.
 * Captures waist feeling, cycle day, cycle status, and craving level.
 */

import React from 'react';
import { WaistFeeling, CycleStatus, CravingLevel } from '../../types';

export interface HormonalStepData {
  waist_feeling?: WaistFeeling;
  cycle_day?: number;
  cycle_status?: CycleStatus;
  craving_level?: CravingLevel;
}

interface HormonalStepProps {
  data: HormonalStepData;
  onChange: (data: HormonalStepData) => void;
}

const WAIST_FEELING_OPTIONS: { value: WaistFeeling; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'bloated', label: 'Bloated' },
  { value: 'tight', label: 'Tight' },
];

const CYCLE_STATUS_OPTIONS: { value: CycleStatus; label: string }[] = [
  { value: 'tracking', label: 'Tracking my cycle' },
  { value: 'not_sure', label: 'Not sure' },
  { value: 'postmenopausal', label: 'Postmenopausal' },
];

const CRAVING_LEVEL_OPTIONS: { value: CravingLevel; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'low', label: 'Low' },
  { value: 'high', label: 'High' },
];

const HormonalStep: React.FC<HormonalStepProps> = ({ data, onChange }) => {
  const update = (partial: Partial<HormonalStepData>) => {
    onChange({ ...data, ...partial });
  };

  const handleCycleStatusChange = (status: CycleStatus) => {
    if (status !== 'tracking') {
      update({ cycle_status: status, cycle_day: undefined });
    } else {
      update({ cycle_status: status });
    }
  };

  return (
    <div className="animate-slide-up">
      <h2 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
        Hormonal Status
      </h2>
      <p className="text-neutral-600 mb-6">
        Optional indicators that help personalize your guidance
      </p>

      {/* Waist Feeling */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          How does your waist feel? <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <div className="grid grid-cols-3 gap-2">
          {WAIST_FEELING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => update({ waist_feeling: data.waist_feeling === option.value ? undefined : option.value })}
              className={`p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                data.waist_feeling === option.value
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-primary-200 bg-white hover:border-primary-400'
              }`}
            >
              <span className="text-sm font-medium text-neutral-700">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Cycle Status */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          Cycle status <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <div className="grid grid-cols-1 gap-2">
          {CYCLE_STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleCycleStatusChange(option.value)}
              className={`p-3 rounded-xl border-2 transition-all duration-200 text-left ${
                data.cycle_status === option.value
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-primary-200 bg-white hover:border-primary-400'
              }`}
            >
              <span className="text-sm font-medium text-neutral-700">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Cycle Day - only shown when tracking */}
      {data.cycle_status === 'tracking' && (
        <div className="mb-6">
          <label htmlFor="cycle-day" className="block text-sm font-medium text-neutral-700 mb-2">
            Cycle day <span className="text-neutral-400 font-normal">(day since period started)</span>
          </label>
          <input
            id="cycle-day"
            type="number"
            value={data.cycle_day ?? ''}
            onChange={(e) => update({
              cycle_day: e.target.value ? parseInt(e.target.value) : undefined
            })}
            className="input w-32"
            placeholder="e.g. 14"
            min="1"
            max="99"
          />
        </div>
      )}

      {/* Craving Level */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          Cravings today? <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <div className="grid grid-cols-3 gap-2">
          {CRAVING_LEVEL_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => update({ craving_level: data.craving_level === option.value ? undefined : option.value })}
              className={`p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                data.craving_level === option.value
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-primary-200 bg-white hover:border-primary-400'
              }`}
            >
              <span className="text-sm font-medium text-neutral-700">{option.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HormonalStep;
